import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Download } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { getErrorMessage } from "@/lib/errors";

import { fmtMoney, fmtDate } from "@/lib/format";
export default function LedgerTallyExport() {
  const { selectedCompanyId } = useCompany() || {};
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/gl/accounts").then(({ data }) => setAccounts(data)).catch(() => setAccounts([]));
  }, []);

  const viewLedger = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ company_id: selectedCompanyId, account_id: accountId });
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const { data } = await api.get(`/gl/ledger?${params.toString()}`);
      setLedger(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const exportTally = async () => {
    try {
      const params = new URLSearchParams({
        company_id: selectedCompanyId, date_from: dateFrom || "2020-01-01", date_to: dateTo || new Date().toISOString().slice(0, 10),
      });
      const { data } = await api.get(`/gl/tally-export?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = "tally-export.xml";
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success("Tally XML export downloaded");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="ledger-tally-export-page">
      <PageHeader title="Ledger & Tally Export" description="Drill into any account's running balance, or export all vouchers as Tally-compatible XML." />
      {!selectedCompanyId ? (
        <EmptyState title="Select a company from the top bar first" testId="ledger-no-company" />
      ) : (
        <>
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
              <div>
                <Label>Account</Label>
                <Select value={accountId} onValueChange={setAccountId}>
                  <SelectTrigger data-testid="ledger-account-select" className="mt-1.5"><SelectValue placeholder="Select account" /></SelectTrigger>
                  <SelectContent>{accounts.filter((a) => !a.is_group).map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>From</Label><Input type="date" data-testid="ledger-date-from-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1.5" /></div>
              <div><Label>To</Label><Input type="date" data-testid="ledger-date-to-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1.5" /></div>
              <div className="flex items-end gap-2">
                <Button type="button" disabled={!accountId || loading} onClick={viewLedger} data-testid="view-ledger-button" className="flex-1">
                  <Search className="h-3.5 w-3.5" /> View
                </Button>
                <Button type="button" variant="outline" onClick={exportTally} data-testid="export-tally-button">
                  <Download className="h-3.5 w-3.5" /> Tally XML
                </Button>
              </div>
            </div>
          </div>

          {ledger && (
            <div className="rounded-xl border border-border bg-card" data-testid="ledger-result-table">
              {ledger.length === 0 ? (
                <EmptyState title="No entries in this range" testId="ledger-empty-state" />
              ) : (
                <Table>
                  <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Reference</TableHead><TableHead>Narration</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {ledger.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-muted-foreground">{fmtDate(row.entry_date)}</TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">{row.reference || "—"}</TableCell>
                        <TableCell className="text-foreground">{row.narration || "—"}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{Number(row.debit) > 0 ? row.debit : ""}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{Number(row.credit) > 0 ? row.credit : ""}</TableCell>
                        <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{fmtMoney(row.running_balance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
